/**
 * On-device heuristic drawing feedback (replaces server-side recognition).
 *
 * Compares the learner's ink mask with the lesson's reference silhouette (both normalised to
 * their bounding boxes) and derives: is-it-a-drawing, ink coverage, placement on the page,
 * shape match (precision / recall with tolerance), symmetry and detail density. The feedback
 * engine turns metrics into one of three encouraging feedback types — segmentation (highlight
 * matching regions), placement (where the drawing sits) and details — using self-written lines.
 * There is no pass/fail: every drawing is celebrated.
 */
import type { ComplimentBank, FeedbackType, SkillId } from './content/model';
import { dilate, inkCount, makeMask, resizeMask, type Img, type Mask } from './capture';
import { hash } from './mailing';
import { fill, localize, t, type L, type Lang } from './text';

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function maskBox(m: Mask): Box | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < m.height; y++)
    for (let x = 0; x < m.width; x++)
      if (m.data[y * m.width + x]) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** Crop the mask to `box`, fit it (keeping aspect) centred in an `n` x `n` square. */
export function normalizeToSquare(m: Mask, box: Box, n = 64): Mask {
  const crop = makeMask(box.w, box.h);
  for (let y = 0; y < box.h; y++) for (let x = 0; x < box.w; x++) crop.data[y * box.w + x] = m.data[(box.y + y) * m.width + box.x + x]!;
  const s = n / Math.max(box.w, box.h);
  const w = Math.max(1, Math.round(box.w * s));
  const h = Math.max(1, Math.round(box.h * s));
  const small = resizeMask(crop, w, h);
  const out = makeMask(n, n);
  const ox = Math.floor((n - w) / 2);
  const oy = Math.floor((n - h) / 2);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) out.data[(oy + y) * n + ox + x] = small.data[y * w + x]!;
  return out;
}

function overlapFraction(a: Mask, bTol: Mask): number {
  let n = 0;
  let hit = 0;
  for (let i = 0; i < a.data.length; i++)
    if (a.data[i]) {
      n++;
      if (bTol.data[i]) hit++;
    }
  return n ? hit / n : 0;
}

/** Left/right mirror agreement of a square mask (1 = perfectly symmetric). */
export function symmetry(m: Mask): number {
  const tol = dilate(m, 2);
  let n = 0;
  let hit = 0;
  for (let y = 0; y < m.height; y++)
    for (let x = 0; x < m.width; x++)
      if (m.data[y * m.width + x]) {
        n++;
        if (tol.data[y * m.width + (m.width - 1 - x)]) hit++;
      }
  return n ? hit / n : 0;
}

export interface DrawingMetrics {
  isDrawing: boolean;
  /** Ink pixels / all pixels of the user's photo. */
  coverage: number;
  /** User drawing bounding box, normalised 0..1 to the photo. */
  box: Box | null;
  /** Centre offset from the page centre (-0.5..0.5). */
  offset: { x: number; y: number };
  /** Bounding box area / page area. */
  size: number;
  /** Shape agreement with the reference: precision, recall, f1 (0..1). */
  precision: number;
  recall: number;
  shapeScore: number;
  symmetry: number;
  referenceSymmetry: number;
  /** User ink density inside the box relative to the reference's (≈1 = similar amount of detail). */
  detailRatio: number;
  /** 3x3 grid of matched cells (normalised to the user box), for highlight overlays. */
  cells: { x: number; y: number; w: number; h: number; match: number }[];
}

const MIN_COVERAGE = 0.003;
const MAX_COVERAGE = 0.55;

export function analyseDrawing(user: Mask, reference: Mask): DrawingMetrics {
  const total = user.width * user.height;
  const ink = inkCount(user);
  const coverage = ink / total;
  const box = maskBox(user);
  const refBox = maskBox(reference);
  const empty: DrawingMetrics = {
    isDrawing: false,
    coverage,
    box: null,
    offset: { x: 0, y: 0 },
    size: 0,
    precision: 0,
    recall: 0,
    shapeScore: 0,
    symmetry: 0,
    referenceSymmetry: 0,
    detailRatio: 0,
    cells: [],
  };
  if (!box || !refBox || coverage < MIN_COVERAGE || coverage > MAX_COVERAGE || box.w < user.width * 0.05 || box.h < user.height * 0.05) return empty;

  const n = 64;
  const u = normalizeToSquare(user, box, n);
  const r = normalizeToSquare(reference, refBox, n);
  const precision = overlapFraction(u, dilate(r, 3));
  const recall = overlapFraction(r, dilate(u, 3));
  const shapeScore = precision + recall ? (2 * precision * recall) / (precision + recall) : 0;
  const uDensity = inkCount(u) / (n * n);
  const rDensity = inkCount(r) / (n * n) || 1e-6;

  const cells: DrawingMetrics['cells'] = [];
  const third = n / 3;
  const uTol = dilate(u, 3);
  for (let cy = 0; cy < 3; cy++)
    for (let cx = 0; cx < 3; cx++) {
      let refInk = 0;
      let hit = 0;
      for (let y = Math.floor(cy * third); y < Math.floor((cy + 1) * third); y++)
        for (let x = Math.floor(cx * third); x < Math.floor((cx + 1) * third); x++)
          if (r.data[y * n + x]) {
            refInk++;
            if (uTol.data[y * n + x]) hit++;
          }
      if (refInk > 4)
        cells.push({
          x: (box.x + (box.w * cx) / 3) / user.width,
          y: (box.y + (box.h * cy) / 3) / user.height,
          w: box.w / 3 / user.width,
          h: box.h / 3 / user.height,
          match: hit / refInk,
        });
    }

  return {
    isDrawing: true,
    coverage,
    box: { x: box.x / user.width, y: box.y / user.height, w: box.w / user.width, h: box.h / user.height },
    offset: { x: (box.x + box.w / 2) / user.width - 0.5, y: (box.y + box.h / 2) / user.height - 0.5 },
    size: (box.w * box.h) / total,
    precision,
    recall,
    shapeScore,
    symmetry: symmetry(u),
    referenceSymmetry: symmetry(r),
    detailRatio: uDensity / rDensity,
    cells,
  };
}

/* --------------------------------- Feedback text --------------------------------- */

export interface FeedbackResult {
  type: FeedbackType;
  isDrawing: boolean;
  /** 1..3 encouragement stars (never 0: every drawing is celebrated). */
  stars: 1 | 2 | 3;
  headline: string;
  nudge: string | null;
  /** Normalised highlight boxes over the photo (segmentation feedback). */
  highlights: { x: number; y: number; w: number; h: number; good: boolean }[];
  /** Placement target (page centre) and where the drawing actually sits, normalised. */
  placement: { target: { x: number; y: number }; actual: { x: number; y: number } } | null;
  reflection: string;
  metrics: DrawingMetrics;
  /** 'colour' feedback (curriculum v2 colouring lessons): colour coverage per zone of the figure. */
  colour?: ColourMetrics;
}

export interface FeedbackContext {
  name: string;
  subject: string;
  lang: Lang;
  skill: SkillId;
  seed: string;
}

const pick = <T>(arr: T[], seed: string): T => arr[hash(seed) % arr.length]!;

export function buildFeedback(m: DrawingMetrics, type: FeedbackType, bank: ComplimentBank, ctx: FeedbackContext): FeedbackResult {
  const vars = { name: ctx.name, subject: ctx.subject };
  const L = (x: Parameters<typeof localize>[0]) => fill(localize(x, ctx.lang), vars);
  const reflection = L(bank.reflection[ctx.skill]);
  if (!m.isDrawing) {
    return { type, isDrawing: false, stars: 1, headline: L(pick(bank.noDrawing, ctx.seed)), nudge: null, highlights: [], placement: null, reflection, metrics: m };
  }
  const stars: 1 | 2 | 3 = m.shapeScore >= 0.55 ? 3 : m.shapeScore >= 0.3 ? 2 : 1;
  let nudge: string | null = null;
  if (type === 'placement') {
    if (m.offset.x < -0.18) nudge = L(bank.nudges.placementLeft);
    else if (m.offset.x > 0.18) nudge = L(bank.nudges.placementRight);
    else if (m.size < 0.08) nudge = L(bank.nudges.placementSmall);
    else if (m.size > 0.8) nudge = L(bank.nudges.placementLarge);
  } else if (type === 'details') {
    if (m.detailRatio < 0.45) nudge = L(bank.nudges.moreDetails);
  } else if (m.referenceSymmetry > 0.8 && m.symmetry < 0.5) {
    nudge = L(bank.nudges.symmetry);
  }
  const pool = type === 'placement' ? bank.placement : type === 'details' ? bank.details : bank.segmentation;
  return {
    type,
    isDrawing: true,
    stars,
    headline: L(pick(pool, ctx.seed)),
    nudge,
    highlights: m.cells.map((c) => ({ x: c.x, y: c.y, w: c.w, h: c.h, good: c.match >= 0.35 })),
    placement: m.box ? { target: { x: 0.5, y: 0.5 }, actual: { x: m.box.x + m.box.w / 2, y: m.box.y + m.box.h / 2 } } : null,
    reflection,
    metrics: m,
  };
}

/* ------------------------------ Colour feedback (v2) ------------------------------ */

/**
 * Curriculum v2 colouring lessons (`scanImage.feedbackType: 'colour'`): compare the colour in the photo
 * with the colour zones of the figure (one mask per art region, all in the same reference frame).
 * Both are normalised to their bounding boxes (like `analyseDrawing`); per zone we measure how much is
 * painted and how light it is on the lit side (top-left, the light source of every lesson) vs the
 * shadow side (bottom-right), and whether a small unpainted highlight was kept on the lit side.
 * No colour at all is fine: the lesson still completes ("Today we skipped colouring").
 */
export interface ColourZoneMetrics {
  /** Share of the zone's pixels that are painted (colour or pencil shading), 0..1. */
  painted: number;
  /** Mean lightness of the painted pixels on the lit / shadow half (0 dark .. 1 light), -1 = none. */
  litLight: number;
  shadeLight: number;
  /** Share of near-paper pixels in the lit quarter of a painted zone (a kept highlight). */
  highlight: number;
}

export interface ColourMetrics {
  /** Enough colour on the drawing to talk about it. */
  coloured: boolean;
  /** Painted share over every zone. */
  coverage: number;
  /** At least one painted zone is clearly darker on its shadow side. */
  shadowDarker: boolean;
  /** A painted zone keeps a small light spot on its lit side. */
  highlightKept: boolean;
  zones: ColourZoneMetrics[];
}

const COLOUR_N = 48;
/** Painted = saturated colour or a pencil / crayon tone clearly darker than the paper. */
const pixelInfo = (img: Img, i: number) => {
  const r = img.data[i * 4]! / 255;
  const g = img.data[i * 4 + 1]! / 255;
  const b = img.data[i * 4 + 2]! / 255;
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const light = 0.299 * r + 0.587 * g + 0.114 * b;
  const sat = mx > 0 ? (mx - mn) / mx : 0;
  return { light, sat, painted: (sat > 0.22 && light < 0.94) || light < 0.62 };
};

/** Bounding box of every non-paper pixel (ink or colour) of a photo. */
function paintedBox(img: Img, ink?: Mask): Box | null {
  const m = makeMask(img.width, img.height);
  for (let i = 0; i < img.width * img.height; i++) if (pixelInfo(img, i).painted || ink?.data[i]) m.data[i] = 1;
  return maskBox(m);
}

/**
 * Colour metrics of a photo (RGBA) against the figure's zone masks (reference frame, same size).
 * `ink` (optional, photo size) helps find the drawing's box when it is mostly line art.
 */
export function analyseColour(photo: Img, zones: Mask[], ink?: Mask): ColourMetrics {
  const none: ColourMetrics = { coloured: false, coverage: 0, shadowDarker: false, highlightKept: false, zones: [] };
  if (!zones.length) return none;
  const union = makeMask(zones[0]!.width, zones[0]!.height);
  for (const z of zones) for (let i = 0; i < z.data.length; i++) if (z.data[i]) union.data[i] = 1;
  const refBox = maskBox(union);
  const box = paintedBox(photo, ink);
  if (!refBox || !box || box.w < photo.width * 0.05 || box.h < photo.height * 0.05) return none;
  const n = COLOUR_N;
  // Zones shrunk by 2 cells: the outline drawn on the zone's edge is line work, not colour.
  const zn = zones.map((z) => {
    const sq = normalizeToSquare(z, refBox, n);
    const outside = makeMask(n, n);
    for (let i = 0; i < sq.data.length; i++) outside.data[i] = sq.data[i] ? 0 : 1;
    const edge = dilate(outside, 2);
    const inner = makeMask(n, n);
    for (let i = 0; i < sq.data.length; i++) inner.data[i] = sq.data[i] && !edge.data[i] ? 1 : 0;
    return inkCount(inner) >= 6 ? inner : sq;
  });
  // Inverse of normalizeToSquare for the photo box: square cell → photo pixel.
  const s = n / Math.max(box.w, box.h);
  const ox = (n - box.w * s) / 2;
  const oy = (n - box.h * s) / 2;
  const at = (sx: number, sy: number) => {
    const x = Math.floor(box.x + (sx + 0.5 - ox) / s);
    const y = Math.floor(box.y + (sy + 0.5 - oy) / s);
    if (x < box.x || y < box.y || x >= box.x + box.w || y >= box.y + box.h) return -1;
    return y * photo.width + x;
  };
  let all = 0;
  let allPainted = 0;
  const out: ColourZoneMetrics[] = zn.map((z) => {
    let cells = 0;
    let painted = 0;
    let lit = 0;
    let litN = 0;
    let shade = 0;
    let shadeN = 0;
    let hiN = 0;
    let hiPaper = 0;
    const zb = maskBox(z);
    if (!zb) return { painted: 0, litLight: -1, shadeLight: -1, highlight: 0 };
    const cx = zb.x + zb.w / 2;
    const cy = zb.y + zb.h / 2;
    for (let y = 0; y < n; y++)
      for (let x = 0; x < n; x++) {
        if (!z.data[y * n + x]) continue;
        const i = at(x, y);
        if (i < 0) continue;
        cells++;
        const px = pixelInfo(photo, i);
        // Diagonal split: light from the top-left.
        const side = (x - cx) / zb.w + (y - cy) / zb.h;
        if (x < cx && y < cy) {
          hiN++;
          if (!px.painted) hiPaper++;
        }
        if (!px.painted) continue;
        painted++;
        if (side < 0) {
          lit += px.light;
          litN++;
        } else {
          shade += px.light;
          shadeN++;
        }
      }
    all += cells;
    allPainted += painted;
    return { painted: cells ? painted / cells : 0, litLight: litN ? lit / litN : -1, shadeLight: shadeN ? shade / shadeN : -1, highlight: hiN ? hiPaper / hiN : 0 };
  });
  const coverage = all ? allPainted / all : 0;
  const paintedZones = out.filter((z) => z.painted >= 0.35);
  return {
    coloured: coverage >= 0.12,
    coverage,
    shadowDarker: paintedZones.some((z) => z.litLight >= 0 && z.shadeLight >= 0 && z.litLight - z.shadeLight >= 0.06),
    highlightKept: paintedZones.some((z) => z.painted >= 0.45 && z.highlight >= 0.06 && z.highlight <= 0.6),
    zones: out,
  };
}

/** Colour feedback lines (self-written). */
export const COLOUR_LINES = {
  shadow: [t('The shadow side is darker — just like real light!', 'Phía bóng đậm hơn rồi — y như ánh sáng thật!'), t('Look, {name}: darker on the shadow side. That makes it look round!', 'Nhìn kìa {name}: phía bóng đậm hơn. Nhờ vậy trông tròn trịa hẳn!')],
  highlight: [t('You kept a highlight! It shines!', 'Con chừa điểm sáng rồi! Lấp lánh quá!'), t('That little light spot makes your {subject} sparkle!', 'Điểm sáng nhỏ xíu làm {subject} của con lấp lánh!')],
  both: [t('Light side, shadow side AND a highlight — a real colourist!', 'Phía sáng, phía bóng VÀ điểm sáng — đúng là hoạ sĩ tô màu!')],
  coloured: [t('What lovely colours, {name}!', 'Màu sắc đẹp quá, {name} ơi!'), t('Your {subject} is full of colour!', '{subject} của con rực rỡ sắc màu!')],
  nudgeShadow: t('Next time, press a bit harder on the side away from the light.', 'Lần sau, ấn mạnh tay hơn một chút ở phía xa ánh sáng nhé.'),
  nudgeHighlight: t('Try leaving a tiny white spot where the light shines.', 'Thử chừa một đốm trắng nhỏ ở chỗ ánh sáng chiếu vào nhé.'),
  skipped: [t('Today we skipped colouring — the drawing is lovely as it is!', 'Hôm nay mình bỏ qua tô màu — tranh vẫn rất đẹp!')],
} satisfies Record<string, L | L[]>;

/** Feedback for a colouring lesson (`type: 'colour'`); never fails, no colour = "skipped colouring". */
export function buildColourFeedback(c: ColourMetrics, m: DrawingMetrics, bank: ComplimentBank, ctx: FeedbackContext): FeedbackResult {
  const vars = { name: ctx.name, subject: ctx.subject };
  const Lx = (x: L) => fill(localize(x, ctx.lang), vars);
  const reflection = Lx(bank.reflection[ctx.skill]);
  const base = { type: 'colour' as const, highlights: [], placement: null, reflection, metrics: m, colour: c };
  if (!c.coloured) return { ...base, isDrawing: true, stars: 1, headline: Lx(pick(COLOUR_LINES.skipped, ctx.seed)), nudge: null };
  const stars: 1 | 2 | 3 = c.shadowDarker && c.highlightKept ? 3 : c.shadowDarker || c.highlightKept ? 2 : 1;
  const pool = c.shadowDarker && c.highlightKept ? COLOUR_LINES.both : c.shadowDarker ? COLOUR_LINES.shadow : c.highlightKept ? COLOUR_LINES.highlight : COLOUR_LINES.coloured;
  const nudge = !c.shadowDarker ? Lx(COLOUR_LINES.nudgeShadow) : !c.highlightKept ? Lx(COLOUR_LINES.nudgeHighlight) : null;
  return { ...base, isDrawing: true, stars, headline: Lx(pick(pool, ctx.seed)), nudge };
}

/* ------------------------------ Circle calibration ------------------------------ */

export type CircleResult = 'great' | 'notRound' | 'unequal' | 'tooSmall' | 'noDrawing';

/**
 * Onboarding "real drawer" check: the learner draws a circle on paper and photographs it.
 * Width ≈ height and points at an even distance from the centre → great.
 */
export function analyseCircle(m: Mask): { result: CircleResult; aspect: number; roundness: number } {
  const box = maskBox(m);
  const coverage = inkCount(m) / (m.width * m.height);
  if (!box || coverage < MIN_COVERAGE || coverage > MAX_COVERAGE) return { result: 'noDrawing', aspect: 0, roundness: 0 };
  const aspect = box.w / box.h;
  if (box.w < m.width * 0.12 && box.h < m.height * 0.12) return { result: 'tooSmall', aspect, roundness: 0 };
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const ds: number[] = [];
  for (let y = 0; y < m.height; y++)
    for (let x = 0; x < m.width; x++)
      if (m.data[y * m.width + x]) {
        // Normalise by the box so ellipses read as "unequal" rather than "not round".
        ds.push(Math.hypot((x - cx) / (box.w / 2), (y - cy) / (box.h / 2)));
      }
  const mean = ds.reduce((a, b) => a + b, 0) / ds.length;
  const sd = Math.sqrt(ds.reduce((a, b) => a + (b - mean) ** 2, 0) / ds.length);
  const roundness = Math.max(0, 1 - sd / Math.max(mean, 1e-6));
  if (roundness < 0.75) return { result: 'notRound', aspect, roundness };
  if (aspect < 0.8 || aspect > 1.25) return { result: 'unequal', aspect, roundness };
  return { result: 'great', aspect, roundness };
}
